import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { type CardProps, HStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { genericMemo } from '@direct-frontend/stdlib';
import { ContainerTreeSizeProvider } from './components/ContainerTreeSizeProvider';
import { flattenNodes } from './lib/flattenNodes';
import { type NodeTemplateRender } from './lib/NodeTemplateRender';
import { type TreeSize } from './lib/types';

export interface TreeProps<Attributes = unknown> extends TestProps {
    items: Attributes[];
    size?: TreeSize;
    selected?: string | undefined;
    isAllExpanded?: boolean;
    renderNode: NodeTemplateRender<Attributes>;
    borderRadius?: CardProps['borderRadius'];
    getIds: (item: Attributes) => {
        id: string;
        parentId: string | undefined;
    };
    sortByKeys: string | string[];
}

/**
 * Компонент отрисовки дерева с иерархией
 * ! Виртуализации нет, прикрутить
 */
export const Tree = genericMemo(function Tree<Attributes = unknown>({
    'data-testid': dataTestId = 'Tree',
    items,
    size = 's',
    selected,
    renderNode,
    isAllExpanded,
    getIds,
    sortByKeys,
}: TreeProps<Attributes>) {
    const { rows: flatNodes } = useMemo(() => {
        const sortedItems = sortBy(items, sortByKeys);

        return flattenNodes<Attributes>(sortedItems, getIds);
    }, [getIds, items, sortByKeys]);

    const [expanded, setExpanded] = useState<Set<string>>(() => {
        if (isAllExpanded) {
            return new Set(flatNodes.map(({ id }) => id));
        }

        return new Set();
    });

    useEffect(() => {
        if (isAllExpanded) {
            setExpanded(new Set(flatNodes.map(({ id }) => id)));
        }
    }, [isAllExpanded, flatNodes]);

    const visibleNodes = useMemo(
        () => flatNodes.filter((node) => node.rendersBefore.every((parent) => expanded.has(parent.id))),
        [flatNodes, expanded]
    );

    const handleToggleExpand = (id: string) => {
        setExpanded((oldState) => {
            const newState = new Set(oldState);

            if (newState.has(id)) {
                newState.delete(id);
            } else {
                newState.add(id);
            }

            return newState;
        });
    };

    const ref = useRef<HTMLElement>(null);

    return (
        <HStack ref={ref} scrollable data-testid={dataTestId} width="max">
            <div style={{ minWidth: '100%' }}>
                <ContainerTreeSizeProvider containerRef={ref}>
                    {visibleNodes.map((node) => (
                        <Fragment key={node.id}>
                            {renderNode({
                                'data-testid': `${dataTestId}.${node.id}`,
                                size,
                                isSelected: node.id === selected,
                                node,
                                isOpen: expanded.has(node.id),
                                onToggle: handleToggleExpand,
                            })}
                        </Fragment>
                    ))}
                </ContainerTreeSizeProvider>
            </div>
        </HStack>
    );
});
